import { AppLink } from '@/shared/components/AppLink';
import { useEffect, useState } from 'react';
import { resolveImageUrl } from '@/shared/utils/imageUtils';

interface MenuImageItem {
  title: string;
  heroImageUrl?: string; // Main model image
  modelImageUrl?: string; // Fallback for main image
  productImageUrl?: string;
  imageUrl?: string; // Product image (or fallback)
  link: string;
  lazyload?: string;
  shouldRefresh?: boolean;
}

interface RoundMenuImageSectionProps {
  data: Record<string, unknown> & {
    title?: string | string[];
    items?: MenuImageItem[];
    blankImageUrl?: string;
    url?: string;
    posterUrl?: string;
    isVideo?: boolean;
    autoplay?: string;
    playsinline?: string;
    loop?: string;
    muted?: string;
  };
  sectionKey: string;
  eventLabel?: string;
  priority?: number;
}

export default function RoundMenuImageSection({
  data,
  sectionKey,
  eventLabel,
  priority,
}: RoundMenuImageSectionProps) {
  const items = (data.items || []) as MenuImageItem[];
  const title = Array.isArray(data.title) ? data.title[0] : data.title;
  const slideInterval = (data.slideInterval as number) || 3000;
  const [mobileCurrent, setMobileCurrent] = useState(0);

  // Video data
  const videoUrl = data.url as string | undefined;
  const posterUrl = data.posterUrl as string | undefined;
  const isVideo = data.isVideo as boolean | undefined;
  const autoplay = data.autoplay as string | undefined;
  const playsinline = data.playsinline as string | undefined;
  const loop = data.loop as string | undefined;
  const muted = data.muted as string | undefined;

  // Mobile fade carousel auto-advance - only for images (not video)
  useEffect(() => {
    if (isVideo || items.length <= 1) return;

    const interval = setInterval(() => {
      setMobileCurrent((prev) => (prev + 1) % items.length);
    }, slideInterval);

    return () => clearInterval(interval);
  }, [items.length, slideInterval, isVideo]);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <section className="py-8 md:py-12">
      <div className="md:px-[30px]">
        {/* Section Title */}
        {title && (
          <div className="container-fluid mb-4 md:mb-5">
            <h2 className="text-base md:text-[24px] font-normal text-center leading-normal md:leading-[30px]">
              <span className="font-primary text-[#9D7792] md:text-[#032C5E]">{title}</span>
            </h2>
          </div>
        )}

        {/* Mobile: Hero Content */}
        <div className="md:hidden w-full">
          {isVideo && videoUrl ? (
            /* Hero Video Container - Single static video */
            <div className="relative w-full" style={{ aspectRatio: '170/100' }}>
              <div className="w-full h-full bg-[#808080] overflow-hidden flex items-center justify-center relative">
                <video
                  poster={posterUrl ? resolveImageUrl(posterUrl) : undefined}
                  className="w-full h-full object-cover"
                  autoPlay={!!autoplay}
                  playsInline={!!playsinline}
                  loop={!!loop}
                  muted={!!muted}
                  preload="auto"
                >
                  <source src={resolveImageUrl(videoUrl)} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          ) : (
            /* Hero Image Container - All images stacked with fade */
            <div className="relative w-full" style={{ aspectRatio: '170/100' }}>
              {items.map((item, index) => {
                const modelImage = item.heroImageUrl || item.modelImageUrl || item.imageUrl || '';
                const isActive = index === mobileCurrent;

                if (!modelImage) return null;

                return (
                  <div
                    key={`mobile-hero-${item.title}-${index}`}
                    className={`absolute inset-0 transition-opacity duration-[800ms] ease-in-out ${
                      isActive ? 'opacity-100 z-10' : 'opacity-0 z-0'
                    }`}
                  >
                    <div className="w-full h-full bg-[#808080] overflow-hidden flex items-center justify-center relative">
                      <img
                        className="w-full h-full object-cover"
                        src={resolveImageUrl(modelImage)}
                        alt={item.title}
                        loading={item.lazyload ? 'lazy' : 'eager'}
                        decoding="async"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Product Items - Three items below hero image */}
          <div className="flex justify-around gap-4 relative z-10 mt-[-30px] px-[5%]">
            {items.map((item, index) => {
              const productImage = item.productImageUrl || item.imageUrl || '';

              return (
                <AppLink
                  key={`mobile-product-${item.title}-${index}`}
                  to={item.link}
                  className="flex flex-col items-center gap-2 ga-event-trigger"
                  data-eventname={`${sectionKey}_${item.title.replace(/\s/g, '')}`}
                  data-pos={`${priority}-${index + 1}`}
                  data-sectionname={eventLabel}
                  data-bannername={item.title}
                  shouldRefresh={item.shouldRefresh}
                >
                  {/* Product Image */}
                  {productImage && (
                    <div className="flex-1 bg-[#FCFCFB] border-[0.5px] border-[#B3848F] rounded-[15px] shadow-[0_2px_7px_rgba(191,160,160,0.25)] text-center overflow-hidden w-full aspect-square flex items-center justify-center px-2 py-2">
                      <img
                        className="w-[70px] h-auto object-contain"
                        src={resolveImageUrl(productImage)}
                        alt={`${item.title} product`}
                        loading={item.lazyload ? 'lazy' : 'eager'}
                        decoding="async"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  {/* Title Label */}
                  <h3 className="text-center font-secondary text-[12px] leading-[16px] text-[#BFB9BD] font-normal m-0">
                    {item.title}
                  </h3>
                </AppLink>
              );
            })}
          </div>
        </div>

        {/* Desktop: Product Showcases */}
        <div className="hidden md:grid md:grid-cols-3 gap-4 md:gap-4">
          {items.map((item, index) => {
            // Use heroImageUrl (preferred), then modelImageUrl, then imageUrl as fallback
            const modelImage = item.heroImageUrl || item.modelImageUrl || item.imageUrl || '';
            // Use productImageUrl if available, otherwise use imageUrl
            const productImage = item.productImageUrl || item.imageUrl || '';

            return (
              <div
                key={`${item.title}-${index}`}
                className={[
                  'flex flex-col transform-gpu will-change-transform',
                  'transition-[opacity,transform] duration-700 ease-out',
                  'motion-reduce:transition-none motion-reduce:transform-none motion-reduce:opacity-100',
                  // Triggered by SectionRenderer's data-inview="true" (desktop only).
                  'opacity-0 translate-y-6',
                  index % 3 === 0 ? '-translate-x-6' : '',
                  index % 3 === 2 ? 'translate-x-6' : '',
                  'md:group-data-[inview=true]:opacity-100 md:group-data-[inview=true]:translate-y-0 md:group-data-[inview=true]:translate-x-0',
                ].join(' ')}
              >
                <AppLink
                  to={item.link}
                  className="items-center gap-4 md:gap-5 hover:opacity-90 transition-opacity ga-event-trigger"
                  data-eventname={`${sectionKey}_${item.title.replace(/\s/g, '')}`}
                  data-pos={`${priority}-${index + 1}`}
                  data-sectionname={eventLabel}
                  data-bannername={item.title}
                  shouldRefresh={item.shouldRefresh}
                >
                  {/* Model Image - Main photograph with gray background */}
                  {modelImage && (
                    <div className="w-full bg-[#808080] rounded-lg overflow-hidden aspect-[605/502] flex items-center justify-center relative">
                      <img
                        className="w-full h-full object-cover"
                        src={resolveImageUrl(modelImage)}
                        alt={item.title}
                        loading={item.lazyload ? 'lazy' : 'eager'}
                        decoding="async"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  {/* Product Image - Isolated product shot in white rounded panel */}
                  {productImage && (
                    <div className="bg-[#FCFCFB] border border-[#FFF8F9] rounded-[20px] shadow-[4px_4px_5px_rgba(124,104,104,0.25)] text-center overflow-hidden w-[40%] h-auto max-w-[200px] flex items-center justify-center px-4 mt-[-40px] mb-4 relative z-10n left-1/2 translate-x-[-50%]">
                      <img
                        className="w-full h-auto max-h-[120px] object-contain"
                        src={resolveImageUrl(productImage)}
                        alt={`${item.title} product`}
                        loading={item.lazyload ? 'lazy' : 'eager'}
                        decoding="async"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  {/* Title Label */}
                  <h3 className="text-center font-secondary text-[14px] leading-[18px] text-[#BFB9BD] font-normal m-0 mt-1">
                    {item.title}
                  </h3>
                </AppLink>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

